import { useState, useMemo } from 'react'
import { Link, router } from '@inertiajs/react'
import confetti from 'canvas-confetti'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CheckCircle2, ArrowRight, Loader2, Copy, Check, Plug } from 'lucide-react'
import type { DiyWorker } from '@/frontend/pages/Dashboard/Integrations/types'

interface Props {
  workers: DiyWorker[]
  credentialId: string
  credentialName: string
  docUrl: string
  onComplete?: (workerName: string) => void
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

function buildMessage(credentialName: string, docUrl: string) {
  return `Olá! Preciso que você se integre com uma API.

📄 Documentação: ${docUrl}
🔑 Credencial salva como: ${credentialName}

Instruções:
1. Leia a documentação da API no link acima
2. Use a credencial "${credentialName}" que já está salva na plataforma para autenticação
3. Faça uma primeira chamada de teste à API e me mostre o resultado para confirmar que a integração está funcionando

Comece pela leitura da documentação e me diga quais endpoints estão disponíveis.`
}

export default function SendMessageStep({ workers, credentialId, credentialName, docUrl, onComplete }: Props) {
  const [sent, setSent] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [copied, setCopied] = useState(false)
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>('')
  const [assigned, setAssigned] = useState(false)

  const message = useMemo(() => buildMessage(credentialName, docUrl), [credentialName, docUrl])
  const selectedWorker = useMemo(() => workers.find((w) => w.id === selectedWorkerId), [workers, selectedWorkerId])
  const hasWhatsApp = selectedWorker?.whatsapp_number
  const canAct = !!selectedWorkerId && !!credentialId && !processing

  const handleAction = () => {
    if (!selectedWorker || !credentialId) return
    setProcessing(true)

    router.post('/dashboard/integrations/requests/assign_credential', {
      credential_id: credentialId,
      digital_worker_id: selectedWorker.id,
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        setProcessing(false)
        // Inertia fires onSuccess on any redirect without top-level errors,
        // including the rate-limit branch that redirects with flash.alert.
        // Treat a present alert as failure so we don't celebrate or mark complete.
        const flash = (page.props as { flash?: { alert?: string | null } }).flash
        if (flash?.alert) return
        onComplete?.(selectedWorker.name)
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } })
        }
        if (hasWhatsApp) {
          window.open(`https://wa.me/${selectedWorker.whatsapp_number}?text=${encodeURIComponent(message)}`, '_blank')
          setSent(true)
        } else {
          setAssigned(true)
        }
      },
      onError: () => setProcessing(false),
    })
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select text for manual copy
    }
  }

  if (sent) {
    return (
      <div className="text-center space-y-4 py-6">
        <CheckCircle2 className="h-12 w-12 text-success mx-auto" />
        <h3 className="font-semibold text-foreground">Credencial associada!</h3>
        <p className="text-sm text-muted-foreground">
          Seu agente vai receber as instruções e começar a integração.
        </p>
        <Button asChild variant="outline">
          <Link href="/dashboard/integrations">
            Voltar para Integrações <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    )
  }

  if (workers.length === 0) {
    return (
      <div className="text-center space-y-4 py-6">
        <p className="text-sm text-muted-foreground">Nenhum agente disponível.</p>
        <Button asChild variant="outline">
          <Link href="/dashboard/digital_workers/new">Criar um agente</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Selecione o agente que vai realizar a integração.
      </p>

      <div className="flex items-center gap-2">
        <Select value={selectedWorkerId} onValueChange={(v) => { setSelectedWorkerId(v); setAssigned(false) }} disabled={assigned}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Selecione um agente..." />
          </SelectTrigger>
          <SelectContent>
            {workers.map((worker) => (
              <SelectItem key={worker.id} value={worker.id}>
                {worker.name}{worker.job_title ? ` — ${worker.job_title}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasWhatsApp ? (
          <Button size="sm" onClick={handleAction} disabled={!canAct}>
            {processing
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</>
              : <><WhatsAppIcon className="h-4 w-4" /> Enviar mensagem</>}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={handleAction} disabled={!canAct || assigned}>
            {processing
              ? <><Loader2 className="h-4 w-4 animate-spin" /> ...</>
              : assigned
                ? <><Check className="h-4 w-4" /> Associado</>
                : <><Plug className="h-4 w-4" /> Associar</>}
          </Button>
        )}
      </div>

      {assigned && !hasWhatsApp && (
        <Card className="bg-muted/50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">Mensagem para o agente</p>
              <Button variant="ghost" size="sm" onClick={handleCopy}>
                {copied
                  ? <><Check className="h-4 w-4" /> Copiado</>
                  : <><Copy className="h-4 w-4" /> Copiar</>}
              </Button>
            </div>
            <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-sans">{message}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
